import { FlexBox, Skeleton, Text } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber, MetricInstrumentData } from 'lib-analytics-ui';
import { DailyConversationsMetric } from 'lib-dashboard-types';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo } from 'react';

import { QueueDashboardMetric } from 'app/views/layout/views/dashboard/views/queue/components/queues/components/metric';

import { useQueueDashboardQueueContext } from '../../../queue/queueDashboardQueue.context';

import styles from './dailyConversationsMetric.module.scss';

type QueueDashboardDailyConversationsMetricProps = {
  metric?: DailyConversationsMetric;
} & PropsWithClassName;

export const QueueDashboardDailyConversationsMetric = memo(
  function QueueDashboardDailyConversationsMetric({
    className,
    metric,
  }: QueueDashboardDailyConversationsMetricProps): JSX.Element {
    const { isSkeleton } = useQueueDashboardQueueContext();

    const {
      average_assignment_time = null,
      total_answered = 0,
      total_received = 0,
    } = metric ?? {};

    const noDataColor =
      !total_answered && !total_received
        ? typography.color.tertiary
        : undefined;

    return (
      <QueueDashboardMetric
        className={className}
        metricType="daily_conversations"
        isSkeleton={isSkeleton}
      >
        <FlexBox flexDirection="column" gap="200">
          {isSkeleton ? (
            <>
              <Skeleton height="24px" width="72px" />
              <Skeleton height="16px" width="144px" />
            </>
          ) : (
            <>
              <div>
                <MetricInstrumentData
                  data={total_answered}
                  color={noDataColor ?? colors.green[500]}
                  format="number"
                />

                <Text.Heading
                  variant="heading-1"
                  as="span"
                  fontSize="600"
                  padding="0 200"
                  color={noDataColor}
                >
                  /
                </Text.Heading>

                <MetricInstrumentData
                  data={total_received}
                  format="number"
                  color={noDataColor}
                />
              </div>

              <Text.Paragraph variant="4" as="div" subdued>
                Avg. assignment time:{' '}
                <Text.Paragraph
                  variant="3"
                  as="span"
                  color={
                    !average_assignment_time
                      ? typography.color.tertiary
                      : undefined
                  }
                  className={styles.average}
                >
                  {average_assignment_time
                    ? formatInstrumentNumber(
                        average_assignment_time,
                        'duration',
                      )
                    : 'n.a.'}
                </Text.Paragraph>
              </Text.Paragraph>
            </>
          )}
        </FlexBox>
      </QueueDashboardMetric>
    );
  },
);
